import type { SaveData } from "../types";

/**
 * Garden Meister サーバー API のクライアント。
 *
 * VITE_API_BASE_URL が未設定のビルドでは enabled が false になり、
 * ゲームは従来どおり localStorage だけで完結する。サーバーは常に「あると便利な同期先」であり、
 * プレイの前提条件ではない。
 */

export const SYNC_KEY = "gardenMeister.sync.v1";

/** 1 リクエストの打ち切り時間。 */
const REQUEST_TIMEOUT_MS = 8000;

export interface SyncCredentials {
  playerId: string;
  token: string;
  /**
   * 「最初からやり直す」直後は true。既存の庭を拾わずに新しい庭を作るための目印。
   * これが無いと、やり直した直後の起動で以前の庭を見つけて復元してしまう。
   */
  startingNew?: boolean;
  /** サーバー側の庭 ID。採番はサーバーが行う。 */
  gardenId: string | null;
  /** 最後にサーバーと一致した revision。楽観的排他制御の基準値。 */
  syncedRevision: number;
}

export interface GardenSummary {
  gardenId: string;
  name: string;
  revision: number;
  schemaVersion: number;
  totalTicks: number;
  /** 庭ごとの休止設定。正はセーブ本体にあり、これは一覧表示用の写し。 */
  paused: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface RemoteGarden {
  gardenId: string;
  revision: number;
  schemaVersion: number;
  updatedAt: number;
  createdAt: number;
  state: SaveData;
}

export interface PublicationInfo {
  published: boolean;
  slug?: string;
  url?: string;
  title?: string;
  authorName?: string;
  description?: string;
  updatedAt?: number;
  viewCount?: number;
}

export class ApiConflictError extends Error {
  constructor(readonly revision: number, readonly updatedAt: number, readonly totalTicks: number, readonly state: SaveData) {
    super("revision_conflict");
  }
}

export class ApiRequestError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

/** ネットワーク到達不能。オフライン扱いで、ローカル保存のみ継続する。 */
export class ApiOfflineError extends Error {}

export class CredentialStore {
  private cached: SyncCredentials | null | undefined;

  constructor(private readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = window.localStorage) {}

  read(): SyncCredentials | null {
    if (this.cached !== undefined) return this.cached;
    try {
      const raw = this.storage.getItem(SYNC_KEY);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      this.cached = isCredentials(parsed) ? parsed : null;
    } catch {
      this.cached = null;
    }
    return this.cached;
  }

  write(credentials: SyncCredentials): void {
    this.cached = credentials;
    try {
      this.storage.setItem(SYNC_KEY, JSON.stringify(credentials));
    } catch {
      // 保存できなくてもセッション中は cached で動作する。
    }
  }

  update(patch: Partial<SyncCredentials>): SyncCredentials | null {
    const current = this.read();
    if (!current) return null;
    const next = { ...current, ...patch };
    this.write(next);
    return next;
  }

  clear(): void {
    this.cached = null;
    try {
      this.storage.removeItem(SYNC_KEY);
    } catch {
      // 削除できなくても致命的ではない。
    }
  }
}

function isCredentials(value: unknown): value is SyncCredentials {
  if (typeof value !== "object" || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.playerId === "string" && typeof record.token === "string" && (record.gardenId === null || typeof record.gardenId === "string") && typeof record.syncedRevision === "number";
}

export class ApiClient {
  readonly baseUrl: string;

  constructor(baseUrl: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "") {
    this.baseUrl = baseUrl.replace(/\/+$/u, "");
  }

  get enabled(): boolean {
    return this.baseUrl.length > 0;
  }

  private async request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
    if (!this.enabled) throw new ApiOfflineError("サーバー同期は無効です。");
    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}/api/v1${path}`, {
        ...init,
        // 応答が返らないまま起動処理を止めないよう、必ず打ち切り時間を持たせる。
        ...(init.signal ? {} : typeof AbortSignal.timeout === "function" ? { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) } : {}),
        headers: {
          ...(init.body ? { "Content-Type": "application/json" } : {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(init.headers ?? {}),
        },
      });
    } catch {
      throw new ApiOfflineError("サーバーに接続できませんでした。");
    }
    if (response.status === 204) return undefined as T;
    const payload: unknown = await response.json().catch(() => null);
    if (response.ok) return payload as T;

    const error = (payload as { error?: { code?: string; message?: string; detail?: unknown } } | null)?.error;
    const code = error?.code ?? "unknown";
    const message = error?.message ?? `サーバーエラー (${response.status})`;
    if (response.status === 409 && code === "revision_conflict") {
      const detail = error?.detail as { revision: number; updatedAt: number; totalTicks: number; state: SaveData } | undefined;
      if (detail) throw new ApiConflictError(detail.revision, detail.updatedAt, detail.totalTicks, detail.state);
    }
    // 5xx は一時的な障害としてオフライン扱いにし、ローカル保存を継続させる。
    if (response.status >= 500) throw new ApiOfflineError(message);
    throw new ApiRequestError(response.status, code, message);
  }

  createPlayer(displayName = ""): Promise<{ playerId: string; token: string }> {
    return this.request("/players", { method: "POST", body: JSON.stringify({ displayName }) });
  }

  listGardens(token: string): Promise<{ gardens: GardenSummary[] }> {
    return this.request("/gardens", { method: "GET" }, token);
  }

  createGarden(token: string, state: SaveData, name = ""): Promise<{ gardenId: string; revision: number; updatedAt: number }> {
    return this.request("/gardens", { method: "POST", body: JSON.stringify({ state, name }) }, token);
  }

  getGarden(token: string, gardenId: string): Promise<RemoteGarden> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}`, { method: "GET" }, token);
  }

  putGarden(token: string, gardenId: string, baseRevision: number, state: SaveData, keepalive = false): Promise<{ revision: number; updatedAt: number }> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}`, { method: "PUT", body: JSON.stringify({ baseRevision, state }), keepalive }, token);
  }

  deleteGarden(token: string, gardenId: string): Promise<{ deleted: boolean }> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}`, { method: "DELETE" }, token);
  }

  renameGarden(token: string, gardenId: string, name: string): Promise<{ gardenId: string; name: string }> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}`, { method: "PATCH", body: JSON.stringify({ name }) }, token);
  }

  createTransferCode(token: string): Promise<{ code: string; expiresAt: number }> {
    return this.request("/transfer-codes", { method: "POST" }, token);
  }

  redeemTransferCode(code: string): Promise<{ playerId: string; token: string }> {
    return this.request("/transfer-codes/redeem", { method: "POST", body: JSON.stringify({ code }) });
  }

  publish(token: string, gardenId: string, meta: { title: string; description: string; authorName: string }): Promise<{ slug: string; url: string; revision: number }> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}/publication`, { method: "PUT", body: JSON.stringify(meta) }, token);
  }

  unpublish(token: string, gardenId: string): Promise<{ unpublished: boolean }> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}/publication`, { method: "DELETE" }, token);
  }

  getPublication(token: string, gardenId: string): Promise<PublicationInfo> {
    return this.request(`/gardens/${encodeURIComponent(gardenId)}/publication`, { method: "GET" }, token);
  }

  listOpenGardens(params: { limit?: number; season?: string; before?: number } = {}): Promise<{ items: unknown[]; nextCursor: number | null }> {
    const query = new URLSearchParams();
    if (params.limit) query.set("limit", String(params.limit));
    if (params.season) query.set("season", params.season);
    if (params.before) query.set("before", String(params.before));
    const suffix = query.toString();
    return this.request(`/open-gardens${suffix ? `?${suffix}` : ""}`, { method: "GET" });
  }

  getOpenGarden(slug: string): Promise<{ slug: string; title: string; authorName: string; description: string; updatedAt: number; snapshot: unknown }> {
    return this.request(`/open-gardens/${encodeURIComponent(slug)}`, { method: "GET" });
  }
}
